import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DayView } from './agenda/DayView'
import { WeekView } from './agenda/WeekView'
import { MonthView } from './agenda/MonthView'
import {
  buildLanesForTechnicians,
  daysFor,
  jobsByTechnician,
  type AgendaView,
} from './agenda/grid-helpers'
import type {
  AgendaResizeJobRequest,
  AgendaSchedule,
  AgendaScheduleJobRequest,
  Job,
  Technician,
} from '@/types'

export type ScheduleJobPayload = AgendaScheduleJobRequest

export type ResizeJobPayload = AgendaResizeJobRequest

export type UnassignJobOptions = {
  insertBeforeJobId?: string
}

export type AgendaMutationHandlers = {
  onScheduleJob: (jobId: string, payload: ScheduleJobPayload) => void
  onUnassignJob: (jobId: string, options?: UnassignJobOptions) => void
  onResizeJob: (jobId: string, payload: ResizeJobPayload) => void
  isMutatingJobId: string | null
}

export interface AgendaGridProps {
  schedule: AgendaSchedule
  technicians: Technician[]
  view: AgendaView
  // Anchor date of the visible window (drives the day list + day-view column).
  anchor: Date
  // Feedback #15 — clicking a job entry opens the right-side drawer (handled by
  // the parent AgendaPage) instead of navigating away to /jobs/:id, so the
  // calendar stays mounted at full width behind the overlay.
  onEntryClick: (job: Job) => void
  onMonthDayClick?: (day: Date) => void
  onScheduleJob?: (jobId: string, payload: ScheduleJobPayload) => void
  onUnassignJob?: (jobId: string, options?: UnassignJobOptions) => void
  onResizeJob?: (jobId: string, payload: ResizeJobPayload) => void
  isMutatingJobId?: string | null
  enableTechnicianColumnReorder?: boolean
  enableDragDropAssignment?: boolean
  enableResizeDuration?: boolean
  onLoadMoreTechnicians?: () => void
  hasMoreTechnicians?: boolean
  isLoadingMoreTechnicians?: boolean
}

// AC-2.3.1 calendar grid: a time-gutter + technician-column weekly scheduler
// (mirrors design-screens/agenda.md). One lane per active technician, jobs
// colour-coded by technician, conflicts highlighted (never blocked). Day view
// positions blocks by time range; Week stacks per day; Month shows counts.
export function AgendaGrid({
  schedule,
  technicians,
  view,
  anchor,
  onEntryClick,
  onMonthDayClick,
  onScheduleJob,
  onUnassignJob,
  onResizeJob,
  isMutatingJobId,
  enableTechnicianColumnReorder = true,
  enableDragDropAssignment = true,
  enableResizeDuration = true,
  onLoadMoreTechnicians,
  hasMoreTechnicians = false,
  isLoadingMoreTechnicians = false,
}: AgendaGridProps) {
  const { t } = useTranslation('jobs')

  const rows = schedule.technicians
  const visibleTechnicians = useMemo(() => {
    const techniciansById = new Map<string, Technician>()
    for (const technician of technicians)
      techniciansById.set(technician.id, technician)
    for (const row of rows) {
      if (!techniciansById.has(row.technician.id)) {
        techniciansById.set(row.technician.id, row.technician)
      }
    }
    return Array.from(techniciansById.values())
  }, [rows, technicians])
  const lanes = useMemo(
    () => buildLanesForTechnicians(visibleTechnicians),
    [visibleTechnicians]
  )
  const jobsByTech = useMemo(() => jobsByTechnician(rows), [rows])
  const days = useMemo(() => daysFor(anchor, view), [anchor, view])

  if (lanes.length === 0) {
    return (
      <div className='py-16 text-center text-muted-foreground'>
        {t('agenda.noTechnicians')}
      </div>
    )
  }

  // The bordered card + top bar are provided by the parent (AgendaPage); this
  // renders just the grid body for the active view.
  return (
    <>
      {view === 'day' && (
        <DayView
          day={anchor}
          lanes={lanes}
          jobsByTech={jobsByTech}
          onEntryClick={onEntryClick}
          onScheduleJob={onScheduleJob}
          onUnassignJob={onUnassignJob}
          onResizeJob={onResizeJob}
          isMutatingJobId={isMutatingJobId}
          enableTechnicianColumnReorder={enableTechnicianColumnReorder}
          enableDragDropAssignment={enableDragDropAssignment}
          enableResizeDuration={enableResizeDuration}
          onLoadMoreTechnicians={onLoadMoreTechnicians}
          hasMoreTechnicians={hasMoreTechnicians}
          isLoadingMoreTechnicians={isLoadingMoreTechnicians}
        />
      )}
      {view === 'week' && (
        <WeekView
          days={days}
          lanes={lanes}
          jobsByTech={jobsByTech}
          onEntryClick={onEntryClick}
          onDayClick={onMonthDayClick}
          onScheduleJob={onScheduleJob}
          onUnassignJob={onUnassignJob}
          onResizeJob={onResizeJob}
          isMutatingJobId={isMutatingJobId}
          enableTechnicianColumnReorder={enableTechnicianColumnReorder}
          enableDragDropAssignment={enableDragDropAssignment}
          enableResizeDuration={enableResizeDuration}
          onLoadMoreTechnicians={onLoadMoreTechnicians}
          hasMoreTechnicians={hasMoreTechnicians}
          isLoadingMoreTechnicians={isLoadingMoreTechnicians}
        />
      )}
      {view === 'month' && (
        <MonthView
          days={days}
          lanes={lanes}
          jobsByTech={jobsByTech}
          onEntryClick={onEntryClick}
          onDayClick={onMonthDayClick}
          onScheduleJob={onScheduleJob}
          onUnassignJob={onUnassignJob}
          onResizeJob={onResizeJob}
          isMutatingJobId={isMutatingJobId}
          enableTechnicianColumnReorder={enableTechnicianColumnReorder}
          enableDragDropAssignment={enableDragDropAssignment}
          enableResizeDuration={enableResizeDuration}
          onLoadMoreTechnicians={onLoadMoreTechnicians}
          hasMoreTechnicians={hasMoreTechnicians}
          isLoadingMoreTechnicians={isLoadingMoreTechnicians}
        />
      )}
    </>
  )
}
